import { put, takeEvery, call } from "redux-saga/effects";

import * as apis from "../../network/apis/rewards";
import {
  getDeletedRewardsListResponse,
  getDiscountAmountsResponse,
  getRechargeAmountsResponse,
  getRewardingReportResponse,
  getRewardsDetailsResponse,
  getRewardsListResponse,
  getVoucherAmountsResponse,
} from "./slice";
import { showSnackMsg } from "../../utils/Helpers";
import { ROUTES_PATHS } from "../../utils/RoutesPaths";
import { setCanShowEmptyState, setSkeletonObject } from "../Loader/slice";

export function* getRewardsListSaga({ payload }) {
  try {
    yield put(getRewardsListResponse({}));
    yield put(setSkeletonObject({ field: "table", value: true }));
    const response = yield call(apis.getRewards, payload);
    const meta = {
      currentPage: +response.headers["current-page"],
      count: +response.headers["total-count"],
      pages: +response.headers["total-pages"],
    };
    const rewards = response.data?.rewards;
    yield put(getRewardsListResponse({ rewards, meta }));
    yield put(setSkeletonObject({ field: "table", value: false }));
    if (rewards.length === 0) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getDeletedRewardsListSaga({ payload }) {
  try {
    yield put(getDeletedRewardsListResponse({}));
    yield put(setSkeletonObject({ field: "table", value: true }));
    const response = yield call(apis.getDeletedRewards, payload);
    const meta = {
      currentPage: +response.headers["current-page"],
      count: +response.headers["total-count"],
      pages: +response.headers["total-pages"],
    };
    const rewards = response.data?.rewards;
    yield put(getDeletedRewardsListResponse({ rewards, meta }));
    yield put(setSkeletonObject({ field: "table", value: false }));
    if (rewards.length === 0) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* addRewardSaga({ payload }) {
  try {
    yield call(apis.addReward, payload.data);
    yield showSnackMsg(payload.successMsg, "success");
    yield payload.navigate(ROUTES_PATHS.liveRewards);
  } catch (err) {
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getRewardDetailsSaga({ payload }) {
  try {
    yield put(setSkeletonObject({ field: "rewards", value: true }));
    const response = yield call(apis.getRewardDetails, payload);
    const reward = response?.data?.reward;
    yield put(getRewardsDetailsResponse(reward));
    yield put(setSkeletonObject({ field: "rewards", value: false }));
    if (!reward) {
      yield put(setCanShowEmptyState(true));
    } else {
      yield put(setCanShowEmptyState(false));
    }
  } catch (err) {
    yield put(setSkeletonObject({ field: "rewards", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* editRewardSaga({ payload }) {
  try {
    const response = yield call(apis.editReward, payload);
    yield showSnackMsg(payload.successMsg, "success");
    const reward = response?.data?.reward;
    yield put(getRewardsDetailsResponse(reward));
  } catch (err) {
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* deleteRewardSaga({ payload }) {
  try {
    yield call(apis.deleteReward, payload.id);
    yield showSnackMsg(payload.successMsg, "success");
    yield payload.navigate(-1);
  } catch (err) {
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getRechargeAmountsSaga() {
  try {
    const response = yield call(apis.getRechargeAmounts);
    yield put(getRechargeAmountsResponse(response.data.amounts));
  } catch (err) {
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getDiscountAmountsSaga() {
  try {
    const response = yield call(apis.getDiscountAmounts);
    yield put(getDiscountAmountsResponse(response.data.offers));
  } catch (err) {
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getVoucherAmountsSaga() {
  try {
    const response = yield call(apis.getVoucherAmounts);
    yield put(getVoucherAmountsResponse(response.data.offers));
  } catch (err) {
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export function* getRewardingReportSaga() {
  try {
    yield put(setSkeletonObject({ field: "table", value: true }));
    const response = yield call(apis.getRewardingReport);
    yield put(getRewardingReportResponse(response.data.super_rewards_data));
    yield put(setSkeletonObject({ field: "table", value: false }));
  } catch (err) {
    yield put(setSkeletonObject({ field: "table", value: false }));
    yield put(setCanShowEmptyState(true));
    yield err.response?.status !== 401 &&
      showSnackMsg(err.response?.data?.message);
  }
}

export default function* RewardsSagas() {
  yield takeEvery("rewards/getRewardsListRequest", getRewardsListSaga);
  yield takeEvery(
    "rewards/getDeletedRewardsListRequest",
    getDeletedRewardsListSaga
  );
  yield takeEvery("rewards/addRewardsRequest", addRewardSaga);
  yield takeEvery("rewards/getRewardsDetailsRequest", getRewardDetailsSaga);
  yield takeEvery("rewards/editRewardsRequest", editRewardSaga);
  yield takeEvery("rewards/deleteRewardsRequest", deleteRewardSaga);
  yield takeEvery("rewards/getRechargeAmountsRequest", getRechargeAmountsSaga);
  yield takeEvery("rewards/getDiscountAmountsRequest", getDiscountAmountsSaga);
  yield takeEvery("rewards/getVoucherAmountsRequest", getVoucherAmountsSaga);
  yield takeEvery("rewards/getRewardingReportRequest", getRewardingReportSaga);
}
